/**
 * useActionGroups Hook
 *
 * Separates actions into primary and secondary groups.
 */

import { useMemo } from "react";
import type { DetailAction, BaseEntity } from "../types/detailPage";
import type { ActionGroups } from "../types/action-rendering";

interface UseActionGroupsProps<T extends BaseEntity> {
  visibleActions: DetailAction<T>[];
  maxActions?: number;
}

/**
 * Hook for grouping actions into primary and secondary
 *
 * @param visibleActions - Filtered visible actions
 * @param maxActions - Maximum number of primary actions
 * @returns Grouped actions
 */
export function useActionGroups<T extends BaseEntity>({
  visibleActions,
  maxActions = 3,
}: UseActionGroupsProps<T>): ActionGroups<T> {
  // Separate primary and secondary actions
  const primaryActions: DetailAction<T>[] = useMemo(() => {
    return visibleActions.filter((action) => action.primary);
  }, [visibleActions]);

  const secondaryActions: DetailAction<T>[] = useMemo(() => {
    return visibleActions.filter((action) => !action.primary);
  }, [visibleActions]);

  return useMemo(() => {
    // If no primary actions, treat first few as primary
    if (primaryActions.length === 0 && secondaryActions.length > 0) {
      return {
        primaryActions: secondaryActions.slice(0, maxActions),
        secondaryActions: secondaryActions.slice(maxActions),
      };
    }

    return { primaryActions, secondaryActions };
  }, [primaryActions, secondaryActions, maxActions]);
}
